import Http from '../../network/Http'
import FacilityCategory from './FacilityCategory'
import {ContentType} from '../../network/Constants'
import {Nullable} from '../../app/TypeHelpers'
import {ApiResponse} from '../../network/response/ApiResponse'
import {Information} from '../../network/response/Information'
import {ContentsForPagination} from '../shared/ContentsForPagination'
import FacilityForAdmin from './FacilityForAdmin'
import {PaginationTableParams} from '../shared/PaginationTableParams'
import NewFacilityForOvernightRental from './NewFacilityForOvernightRental'
import {DetailsWithInformation} from '../../network/response/DetailsWithInformation'
import FacilityForReservation from './FacilityForReservation'
import {FACILITY_CATEGORY} from '../../CodeMaster'
import NewFacilityForDurationRental from './NewFacilityForDurationRental'
import FacilityDetails from './FacilityDetails'
import UpdateFacilityForOvernightRental from './UpdateFacilityForOvernightRental'
import UpdateFacilityForDurationRental from './UpdateFacilityForDurationRental'
import FacilityCardInfo from './FacilityCardInfo'
import FacilityInfoDetails from './FacilityInfoDetails'

export default class FacilitiesRepository {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    async registerOvernightRentalFacility(
        condominiumCd: string,
        newFacility: NewFacilityForOvernightRental,
    ): Promise<DetailsWithInformation<number>> {
        return await this.registerFacility(
            `/api/admin/condominiums/${condominiumCd}/overnight-rental-facilities`,
            newFacility,
        )
    }

    async registerDurationRentalFacility(
        condominiumCd: string,
        newFacility: NewFacilityForDurationRental,
    ): Promise<DetailsWithInformation<number>> {
        return await this.registerFacility(
            `/api/admin/condominiums/${condominiumCd}/duration-rental-facilities`,
            newFacility,
        )
    }

    private async registerFacility(
        endPoint: string,
        newFacility: NewFacilityForDurationRental | NewFacilityForOvernightRental,
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            endPoint,
            newFacility,
            ContentType.JSON,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async updateOvernightRentalFacility(
        condominiumCd: string,
        facilityId: number,
        updateFacility: UpdateFacilityForOvernightRental,
    ): Promise<DetailsWithInformation<number>> {
        return await this.updateFacility(
            `/api/admin/condominiums/${condominiumCd}/overnight-rental-facilities/${facilityId}`,
            updateFacility,
        )
    }

    async updateDurationRentalFacility(
        condominiumCd: string,
        facilityId: number,
        updateFacility: UpdateFacilityForDurationRental,
    ): Promise<DetailsWithInformation<number>> {
        return await this.updateFacility(
            `/api/admin/condominiums/${condominiumCd}/duration-rental-facilities/${facilityId}`,
            updateFacility,
        )
    }

    private async updateFacility(
        endPoint: string,
        updateFacility: UpdateFacilityForOvernightRental | UpdateFacilityForDurationRental,
    ): Promise<DetailsWithInformation<number>> {
        const apiResponseJSON: ApiResponse<number> = await this.http.put(
            endPoint,
            updateFacility,
        )

        const apiResponse: ApiResponse<number> =
            ApiResponse.from(
                apiResponseJSON,
                (details: any): number => details,
            )

        return new DetailsWithInformation<number>(
            apiResponse.details,
            apiResponse.informations[0],
        )
    }

    async getFacilityCategoriesByCondominiumCd(condominiumCd: string): Promise<FacilityCategory[]> {
        const response: ApiResponse<any[]> =
            await this.http.get(`/api/condominiums/${condominiumCd}/facilities/categories`)
        return response.details
            .map(categoryId => `${categoryId}`)
            .map(categoryId => FACILITY_CATEGORY.codeToEnumConstants(categoryId))
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(category => new FacilityCategory(Number(category.value), category.label))
    }

    async getFacilitiesForAdmin(
        condominiumCd: string,
        categoryId: Nullable<string>,
        params: Nullable<PaginationTableParams>,
    ): Promise<FacilitiesForPagination> {
        const queries: string[] = []

        if (categoryId !== null) {
            queries.push(`category-id=${categoryId}`)
        }

        if (params != null) {
            params.sortOrders.forEach((sortOrder) => {
                if (sortOrder.sortDirection !== false) {
                    const value: string = encodeURIComponent(`${sortOrder.sortKey}:${sortOrder.sortDirection}`)
                    queries.push(`sort-order=${value}`)
                }
            })

            queries.push(`limit=${params.limit}`)
            queries.push(`offset=${params.offset}`)
        }

        let queryString: string = ''
        if (queries.length !== 0) {
            queryString = '?' + queries.join('&')
        }

        const response: ApiResponse<FacilitiesForPagination> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities${queryString}`)
        const apiResponse: ApiResponse<FacilitiesForPagination> =
            ApiResponse.fromContentsForPagination(response, FacilityForAdmin.from)
        return apiResponse.details
    }

    async getFacilityDetails(condominiumCd: string, facilityId: number): Promise<FacilityDetails> {
        const response: ApiResponse<FacilityDetails> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}`)
        const apiResponse: ApiResponse<FacilityDetails> =
            ApiResponse.from(response, FacilityDetails.from)
        return apiResponse.details
    }

    async getFacilityInfoDetails(condominiumCd: string, facilityId: number): Promise<FacilityInfoDetails> {
        const response: ApiResponse<FacilityInfoDetails> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}?returnType=FacilityInfoDetails`)
        const apiResponse: ApiResponse<FacilityInfoDetails> =
            ApiResponse.from(response, FacilityInfoDetails.from)
        return apiResponse.details
    }

    async duplicateFacility(condominiumCd: string, facilityId: number): Promise<Information> {
        const json: ApiResponse<void> = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/duplicate`,
            {},
            ContentType.JSON,
        )
        const apiResponse: ApiResponse<void> = ApiResponse.from(json)
        return apiResponse.informations[0]
    }

    async getFacilityForReservation(
        condominiumCd: string,
        facilityId: number,
    ): Promise<FacilityForReservation> {
        const responseJson: ApiResponse<FacilityForReservation> =
            await this.http.get(`/api/condominiums/${condominiumCd}/facilities/${facilityId}`)
        const apiResponse: ApiResponse<FacilityForReservation> = ApiResponse.from(
            responseJson,
            FacilityForReservation.from,
        )
        return apiResponse.details
    }

    async getFacilityForReservationIfExist(
        condominiumCd: string,
        facilityId: number,
    ): Promise<ApiResponse<Nullable<FacilityForReservation>>> {
        const responseJson: ApiResponse<FacilityForReservation> =
            await this.http.get(
                `/api/condominiums/${condominiumCd}/facilities/${facilityId}?allowFacilityNotExist=true`
            )
        if (responseJson.details === null) {
            return new ApiResponse(null, [])
        }
        return ApiResponse.from(responseJson, FacilityForReservation.from)
    }

    async getFacilityCardInfos(condominiumCd: string): Promise<FacilityCardInfo[]> {
        const responseJson: ApiResponse<FacilityCardInfo[]> =
            await this.http.get(`/api/condominiums/${condominiumCd}/facilities/cards`)
        const apiResponse: ApiResponse<FacilityCardInfo[]> = ApiResponse.fromArray(
            responseJson,
            FacilityCardInfo.from,
        )
        return apiResponse.details
    }

    async deleteFacility(
        condominiumCd: string,
        facilityId: number,
    ): Promise<boolean> {
        await this.http.delete(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}`)
        return true
    }

    async getPublicFacilitiesForAdmin(condominiumCd: string): Promise<FacilityForAdmin[]> {
        const response: ApiResponse<FacilityForAdmin[]> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities?release-setting=public`)
        const apiResponse: ApiResponse<FacilityForAdmin[]> =
            ApiResponse.fromArray(response, FacilityForAdmin.from)
        return apiResponse.details
    }
}

export type FacilitiesForPagination = ContentsForPagination<FacilityForAdmin>
